import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Select,
  Stack,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { BooleanValueFields } from "@/pages/talent/goals/components/BooleanValueFields.tsx";
import { CurrencyValueFields } from "@/pages/talent/goals/components/CurrencyValueFields.tsx";
import { createKeyResultSchema } from "@/pages/talent/goals/components/key-result-form.types.ts";
import { NumberValueFields } from "@/pages/talent/goals/components/NumberValueFields.tsx";
import { PercentageValueFields } from "@/pages/talent/goals/components/PercentageValueFields.tsx";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useGoal, useKeyResultSave } from "@/queries/goals.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiGoalConfidenceLevel,
  ApiGoalKeyResultPayload,
  ApiGoalMetricType,
  GoalMetricTypeItems,
} from "@/types/goals.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface GoalKeyResultEditModalProps extends ModalProps {
  goalId: number;
  keyResultId?: number;
}

export function GoalKeyResultEditModal({
  goalId,
  keyResultId,
  ...props
}: GoalKeyResultEditModalProps) {
  const { close } = useModals();
  const { t } = useTranslation();
  const { id } = props;

  const { data: goal } = useGoal(goalId);
  const keyResult = useMemo(
    () => goal?.goal_key_results?.find((kr) => kr.id === keyResultId),
    [goal, keyResultId]
  );

  const keyResultSave = useKeyResultSave();
  const { data: employeeMe } = useEmployeeMe();

  const { addRecent } = useRecent();

  const metricTypeOptions = GoalMetricTypeItems();
  // const confidenceLevelOptions = GoalConfidenceLevelItems();

  const formHook = useForm({
    mode: "onSubmit",
    resolver: zodResolver(createKeyResultSchema(t)),
    defaultValues: {
      title: "",
      description: "",
      metric_type: ApiGoalMetricType.number,
      start_value: null,
      target_value: null,
      current_value: null,
      currency: employeeMe?.organization?.currency ?? null,
      confidence_level: ApiGoalConfidenceLevel.on_track,
      weight: 1,
      weight_mode: "default",
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    formState: { errors, dirtyFields },
  } = formHook;

  useEffect(() => {
    if (keyResult) {
      setValue("title", keyResult.title);
      setValue("description", keyResult.description ?? "");
      setValue("metric_type", keyResult.metric_type);
      setValue("confidence_level", keyResult.confidence_level);
      const krWeight = Number(keyResult.weight);
      setValue("weight", krWeight);
      setValue("weight_mode", krWeight !== 1 ? "custom" : "default");

      const getValueByType = (
        value: typeof keyResult.start_value,
        type: ApiGoalMetricType
      ): number | null => {
        if (!value) return null;
        switch (type) {
          case ApiGoalMetricType.boolean:
            return value.value_bool ? 1 : 0;
          case ApiGoalMetricType.number:
          case ApiGoalMetricType.percentage:
          case ApiGoalMetricType.currency:
            return value.value_float ? Number(value.value_float) : null;
          default:
            return null;
        }
      };

      if (
        keyResult.metric_type === ApiGoalMetricType.currency &&
        keyResult.start_value?.value_text
      ) {
        setValue("currency", keyResult.start_value.value_text);
      } else {
        setValue("currency", keyResult.currency ?? null);
      }

      setValue("start_value", getValueByType(keyResult.start_value, keyResult.metric_type));
      setValue("target_value", getValueByType(keyResult.target_value, keyResult.metric_type));
    }
  }, [keyResult, setValue]);

  const metricType = watch("metric_type");
  const weightMode = watch("weight_mode");

  useEffect(() => {
    if (!keyResult) {
      if (metricType === ApiGoalMetricType.boolean) {
        setValue("start_value", 0);
        setValue("current_value", 0);
        setValue("target_value", 1);
      } else {
        setValue("start_value", null);
        setValue("current_value", null);
        setValue("target_value", null);
      }
    }
  }, [metricType, keyResult, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    const buildValue = (
      value: number | null | undefined,
      type: ApiGoalMetricType,
      currency?: string | null
    ) => {
      if (value === null || value === undefined) return null;

      switch (type) {
        case ApiGoalMetricType.boolean:
          return { value_bool: Boolean(value) };
        case ApiGoalMetricType.currency:
          return { value_float: value, value_text: currency ?? null };
        case ApiGoalMetricType.number:
        case ApiGoalMetricType.percentage:
        default:
          return { value_float: value };
      }
    };

    const metricType = data.metric_type as ApiGoalMetricType;
    const currencyCode = metricType === ApiGoalMetricType.currency ? data.currency : null;

    const startValue = buildValue(data.start_value, metricType, currencyCode);
    const targetValue = buildValue(data.target_value, metricType, currencyCode);

    const payload: ApiGoalKeyResultPayload = {
      title: data.title,
      description: data.description || null,
      metric_type: metricType,
      start_value: startValue,
      target_value: targetValue,
      confidence_level: data.confidence_level as ApiGoalConfidenceLevel,
      weight: data.weight.toString(),
    };

    if (!keyResultId) {
      const currentValueRaw = data.current_value ?? data.start_value;
      payload.current_value = buildValue(currentValueRaw, metricType, currencyCode);
    }

    try {
      const savedKeyResult = await keyResultSave.mutateAsync({ goalId, keyResultId, payload });

      if (metricType === ApiGoalMetricType.currency && dirtyFields.currency && data.currency) {
        addRecent([{ type: ApiRecentKey.currency, values: [data.currency] }]);
      }

      analyticsService.trackEvent(
        keyResultId ? analyticEvents.goals.metricEdited : analyticEvents.goals.metricCreated,
        {
          [analyticProperties.id]: savedKeyResult.id,
        }
      );

      close(id);
    } catch {
      return;
    }
  });

  const renderValueFields = () => {
    switch (metricType) {
      case ApiGoalMetricType.boolean:
        return <BooleanValueFields formHook={formHook} isCreating={!keyResultId} />;
      case ApiGoalMetricType.percentage:
        return <PercentageValueFields formHook={formHook} isCreating={!keyResultId} />;
      case ApiGoalMetricType.currency:
        return <CurrencyValueFields formHook={formHook} isCreating={!keyResultId} />;
      default:
        return <NumberValueFields formHook={formHook} isCreating={!keyResultId} />;
    }
  };

  return (
    <Modal
      withCloser
      closeByEscEnabled
      {...props}
      title={keyResultId ? t("goals:edit_metric") : t("goals:add_metric")}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={onSubmit}
            isLoading={keyResultSave.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Input
            {...register("title")}
            label={t("common:title")}
            required
            error={errors.title?.message}
          />

          <Textarea
            {...register("description")}
            label={t("common:description")}
            error={errors.description?.message}
          />

          <Title size={"xs"} header={t("goals:values")} paddingTop paddingBottom />
          <Controller
            control={control}
            name="metric_type"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("goals:metric_type")}
                emptyTitle={t("common:select")}
                options={metricTypeOptions}
                value={value}
                onChange={onChange}
                error={errors.metric_type?.message}
              />
            )}
          />

          {renderValueFields()}

          <Title size={"xs"} header={t("common:configuration")} paddingTop paddingBottom />

          <Controller
            control={control}
            name="weight_mode"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("goals:weight")}
                emptyTitle={t("common:select")}
                value={value}
                options={[
                  { id: "default", title: t("common:weight_default") },
                  { id: "custom", title: t("common:weight_custom") },
                ]}
                onChange={(v) => {
                  onChange(v);
                  if (v === "default") {
                    setValue("weight", 1);
                  }
                }}
              />
            )}
          />

          {weightMode === "custom" && (
            <Input
              {...register("weight", {
                setValueAs: (value) => (value === "" ? undefined : Number(value)),
              })}
              type="number"
              step={0.01}
              min={0}
              max={999.99}
              label={t("goals:weight")}
              error={errors.weight?.message}
            />
          )}
        </Stack>
      </form>
    </Modal>
  );
}
